import * as XLSX from "xlsx";

import {
  cellToString,
  findHeaderIndex,
  normalizeHeader,
  parseAmount,
  parseTxnDate,
  readBankWorkbook,
  readBankWorkbookFromText,
  sheetToAoa,
} from "./bank-statement-parse-utils";
import {
  BANK_SKIP_REASON_DEBIT,
  BankParseError,
  type BankCreditTxn,
  type BankParseResult,
  type BankSkippedRow,
} from "./bank-statement-types";

const PNB_SHEET_NAME = "OpTransactionHistory";

type HeaderMap = {
  txnNo: number;
  txnDate: number;
  description: number;
  drAmount: number;
  crAmount: number;
};

function detectHeaderMap(row: unknown[]): HeaderMap | null {
  const cells = row.map(normalizeHeader);
  const txnNo = findHeaderIndex(cells, [
    "txn no",
    "txn number",
    "transaction no",
    "transaction number",
  ]);
  const txnDate = findHeaderIndex(cells, ["txn date", "transaction date"]);
  const description = findHeaderIndex(cells, ["description", "narration", "particulars"]);
  const drAmount = findHeaderIndex(cells, ["dr amount", "debit amount"]);
  const crAmount = findHeaderIndex(cells, ["cr amount", "credit amount"]);

  if (txnNo < 0 || txnDate < 0 || drAmount < 0 || crAmount < 0) return null;
  return {
    txnNo,
    txnDate,
    description: description >= 0 ? description : -1,
    drAmount,
    crAmount,
  };
}

function isNoiseRow(row: unknown[]): boolean {
  const joined = row.map(cellToString).filter(Boolean).join(" ").toLowerCase();
  if (!joined) return true;
  return (
    joined.includes("account statement for account number") ||
    joined.includes("page no") ||
    joined.includes("branch details") ||
    joined.includes("customer details") ||
    joined.includes("statement period") ||
    joined.includes("unless constituent notifies") ||
    joined.includes("computer generated") ||
    joined.includes("abbreviations are as under") ||
    joined.startsWith("qab:") ||
    joined.startsWith("br:") ||
    joined.startsWith("ret:") ||
    joined.includes("please ensure that all the cheque") ||
    joined.includes("customers are requested") ||
    joined.includes("please maintain minimum average balance") ||
    joined.includes("pls note penal interest")
  );
}

function rowLooksLikeTxn(map: HeaderMap, row: unknown[]): boolean {
  const txnNo = cellToString(row[map.txnNo]);
  const txnDateRaw = cellToString(row[map.txnDate]);
  const drRaw = cellToString(row[map.drAmount]);
  const crRaw = cellToString(row[map.crAmount]);
  if (!txnNo && !txnDateRaw && !drRaw && !crRaw) return false;
  if (isNoiseRow(row)) return false;
  if (detectHeaderMap(row)) return false;
  return Boolean(txnNo || txnDateRaw || drRaw || crRaw);
}

/**
 * Pure parser over sheet rows (array-of-arrays). Used by workbook/CSV entry points and tests.
 */
export function parsePnbStatementRows(rows: unknown[][]): BankParseResult {
  let headerMap: HeaderMap | null = null;
  let headerRowNumber = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const map = detectHeaderMap(rows[i] ?? []);
    if (map) {
      headerMap = map;
      headerRowNumber = i + 1;
      break;
    }
  }

  if (!headerMap) {
    throw new BankParseError(
      'Could not find a PNB header row with "Txn No.", "Txn Date", "Dr Amount", and "Cr Amount".'
    );
  }

  const credits: BankCreditTxn[] = [];
  const skipped: BankSkippedRow[] = [];

  for (let i = headerRowNumber; i < rows.length; i += 1) {
    const row = rows[i] ?? [];
    const rowNumber = i + 1;

    if (!rowLooksLikeTxn(headerMap, row)) continue;

    const txnNo = cellToString(row[headerMap.txnNo]);
    const txnDateRaw = cellToString(row[headerMap.txnDate]);
    const description = headerMap.description >= 0 ? cellToString(row[headerMap.description]) : "";
    const drAmount = parseAmount(row[headerMap.drAmount]);
    const crAmount = parseAmount(row[headerMap.crAmount]);

    if (drAmount == null || crAmount == null) {
      skipped.push({
        reason: "Invalid amount",
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw: txnDateRaw || undefined,
        description: description || undefined,
      });
      continue;
    }

    const txnDate = parseTxnDate(txnDateRaw);
    if (!txnDate) {
      skipped.push({
        reason: "Invalid or missing txn date",
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw: txnDateRaw || undefined,
        description: description || undefined,
        amount: crAmount > 0 ? crAmount : drAmount > 0 ? drAmount : undefined,
      });
      continue;
    }

    if (drAmount > 0) {
      skipped.push({
        reason: BANK_SKIP_REASON_DEBIT,
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw,
        description: description || undefined,
        amount: drAmount,
      });
      continue;
    }

    if (crAmount <= 0) {
      skipped.push({
        reason: "No credit amount",
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw,
        description: description || undefined,
      });
      continue;
    }

    credits.push({
      txnNo,
      txnDate,
      description,
      amount: crAmount,
    });
  }

  return { credits, skipped };
}

function pickPnbSheet(wb: XLSX.WorkBook): XLSX.WorkSheet {
  const preferred =
    wb.SheetNames.find((name) => name.trim().toLowerCase() === PNB_SHEET_NAME.toLowerCase()) ??
    wb.SheetNames[0];
  if (!preferred) {
    throw new BankParseError("The file has no sheets.");
  }
  const sheet = wb.Sheets[preferred];
  if (!sheet) {
    throw new BankParseError(`Sheet "${preferred}" is missing.`);
  }
  return sheet;
}

export function parsePnbWorkbook(wb: XLSX.WorkBook): BankParseResult {
  return parsePnbStatementRows(sheetToAoa(pickPnbSheet(wb)));
}

export function parsePnbStatementBuffer(buf: ArrayBuffer | Uint8Array): BankParseResult {
  return parsePnbWorkbook(readBankWorkbook(buf));
}

export function parsePnbStatementCsvText(text: string): BankParseResult {
  return parsePnbWorkbook(readBankWorkbookFromText(text));
}

export async function parsePnbStatementFile(file: File): Promise<BankParseResult> {
  const buf = await file.arrayBuffer();
  return parsePnbStatementBuffer(buf);
}
